/**
 * O QR do segundo fator.
 *
 * A API já devolvia a URI `otpauth://` desde o 3.8 — e a tela a guardava sem nunca
 * a desenhar, oferecendo só o segredo em texto para digitar à mão. Digitar 32
 * caracteres base32 num celular é onde a pessoa erra, desiste ou cadastra errado e
 * só descobre no login seguinte. O fundador pediu o QR no primeiro uso real, e tinha
 * razão: apontar a câmera é o caminho normal, e o texto é o plano B.
 *
 * **Sem `dangerouslySetInnerHTML`.** A biblioteca oferece um `createSvgTag()` que
 * devolve marcação pronta, e usá-lo obrigaria a injetar HTML de string. Aqui se lê o
 * `isDark(linha, coluna)` e se monta o SVG como elemento React — o mesmo desenho,
 * sem abrir a porta que a injeção de HTML abre.
 */
import { useMemo } from 'react'
import gerarQr from 'qrcode-generator'

/**
 * A "zona de silêncio": 4 módulos de margem clara em volta, exigidos pelo padrão.
 * Sem ela o leitor pode não achar as bordas — é a causa clássica de QR que "não lê"
 * em tela, quando o desenho encosta no fundo.
 */
const MARGEM = 4

/**
 * Correção de erro **M** (~15%): o nível que os aplicativos autenticadores usam.
 * Mais correção engrossaria o desenho sem ganho aqui — não é etiqueta que amassa.
 */
const CORRECAO = 'M'

/** O desenho em UM caminho SVG: um módulo escuro vira um quadrado de lado 1. */
export function caminhoDoQr(texto: string): { readonly modulos: number; readonly d: string } {
  const qr = gerarQr(0, CORRECAO)
  qr.addData(texto)
  qr.make()

  const modulos = qr.getModuleCount()
  let d = ''
  for (let linha = 0; linha < modulos; linha += 1) {
    for (let coluna = 0; coluna < modulos; coluna += 1) {
      if (qr.isDark(linha, coluna)) d += `M${String(coluna)} ${String(linha)}h1v1h-1z`
    }
  }
  return { modulos, d }
}

interface Props {
  readonly texto: string
  readonly rotulo: string
  readonly className?: string
}

export function Qr({ texto, rotulo, className }: Props) {
  const { modulos, d } = useMemo(() => caminhoDoQr(texto), [texto])
  const lado = modulos + MARGEM * 2

  return (
    <svg
      viewBox={`0 0 ${String(lado)} ${String(lado)}`}
      role="img"
      aria-label={rotulo}
      data-teste="qr-2fa"
      className={className}
      /* `crispEdges`: sem isto o navegador suaviza as bordas dos módulos e o leitor
         perde contraste nos tamanhos pequenos. */
      shapeRendering="crispEdges"
    >
      {/* O fundo claro é do PRÓPRIO desenho, e não do cartão atrás: um QR escuro
          sobre fundo transparente some se alguém puser este componente noutro lugar. */}
      <rect width={lado} height={lado} fill="#ffffff" />
      <path transform={`translate(${String(MARGEM)} ${String(MARGEM)})`} d={d} fill="#0B1B33" />
    </svg>
  )
}
